import { useEffect, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trophy, Users, ArrowLeft, ArrowRight, ShieldCheck, Flag, Globe } from 'lucide-react';
import { api, type Me, type Competition } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { PageWrapper } from '../components/PageWrapper';
import { CreateClanForm } from '../components/CreateClanForm';
import { useWorkspace } from '../lib/workspace';
import { useEffectiveMe } from '../lib/viewAs';
import { isOrgManagerOfEntity } from '../lib/orgAccess';
import { canRegisterClan } from '../lib/roles';
import { JOINABLE_COMP_STATUSES } from './JoinCompScreen';

export function MyCompDetail() {
  const { compId: rawCompId } = useParams<{ compId: string }>();
  const compId = rawCompId ? decodeURIComponent(rawCompId) : '';
  const { setActiveCompId } = useWorkspace();
  const qc = useQueryClient();

  useEffect(() => {
    if (compId) setActiveCompId(compId);
  }, [compId, setActiveCompId]);

  const { data: me } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me });
  // View-as-aware copy — canManageClans below reads THIS, not the raw query
  // result. Identity no-op whenever no view-as is active (lib/viewAs.ts's
  // useEffectiveMe) — real behaviour is unchanged.
  const effectiveMe = useEffectiveMe(me);
  const { data: allComps } = useQuery({
    queryKey: ['competitions'],
    queryFn: api.competitions,
  });
  const { data: clans, isLoading: clansLoading } = useQuery({
    queryKey: ['compClans', compId],
    queryFn: () => api.competitionClanList(compId),
    enabled: !!compId,
  });

  const comp = useMemo<Competition | undefined>(
    () => (allComps ?? []).find(c => c.id === compId),
    [allComps, compId],
  );

  if (!compId) return <Navigate to="/me/comp" replace />;
  if (!me || !allComps) return <PageSpinner />;

  // effectiveMe, NOT the raw row. A founder previewing a lower persona used to
  // keep their OWN comp memberships here, so "view as member" still opened the
  // manage-clans controls off the founder's real COMP_OWNER grant — the
  // preview was the founder wearing a different label. applyViewAs now
  // simulates comp memberships away with everything else scoped.
  const compMembership = (effectiveMe?.competitionMemberships ?? []).find(m => m.compId === compId);
  // Routed through the SINGLE shared orgAccess helper (isOrgManagerOfEntity)
  // instead of hand-rolling the founder/admin/org-membership check here —
  // it mirrors the backend's canManageOrgCapability exactly (founder/admin
  // short-circuit, then org-membership) and is view-as-aware via effectiveMe
  // (a founder previewing "org admin (scoped)" of this comp's org gets in;
  // previewing a plain lower role does not). Comp-level ownership
  // (COMP_OWNER/COMP_ADMIN) is a separate, comp-scoped grant with no
  // is ALSO view-as-aware now (see compMembership above) — it is a real
  // server-honoured grant (POST /competitions/:compId/clans accepts any comp
  // membership), so it stays in the gate, but a simulated persona must not
  // inherit the founder's copy of it.
  const canManageClans =
    isOrgManagerOfEntity(effectiveMe, comp) ||
    compMembership?.role === 'COMP_OWNER' ||
    compMembership?.role === 'COMP_ADMIN';

  // Doorway into JoinCompScreen's self-serve register-my-clan flow
  // (registration-hole closure, 2026-07-17). Deliberately the RAW platform
  // role (not effectiveMe/view-as) — same reasoning as LeaderPanel's
  // canEnterComp: registerMyClanInComp is leader-gated server-side regardless
  // of which comp a founder happens to be inspecting. Also requires the comp
  // still be joinable (same PENDING/LIVE set JoinCompScreen itself filters
  // on) — a CLOSED comp shouldn't offer a button that leads to a dead end.
  const canRegisterHere = canRegisterClan(me.role, me.clanName) && !!comp && JOINABLE_COMP_STATUSES.has(comp.status);

  const refreshClans = () => qc.invalidateQueries({ queryKey: ['compClans', compId] });

  return (
    <PageWrapper maxWidth="3xl" spacing="md">
      <Link
        to="/me/comp"
        className="inline-flex items-center gap-1 font-display text-[11px] font-black uppercase tracking-wider text-iron-400 hover:text-iron-200"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={2.5} /> back to My Comp
      </Link>

      <div className="flex items-center gap-3">
        <Trophy className="h-6 w-6 text-forge-400" />
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight text-white">
            {comp?.name ?? compId.replace(/^COMP#/, '')}
          </h1>
          {comp?.orgId && (
            <p className="font-mono text-[10px] uppercase tracking-wider text-iron-500">
              org: {comp.orgId.replace(/^ORG#/, '')}
            </p>
          )}
        </div>
        {canRegisterHere && (
          <Link
            to={`/gate/join-comp?compId=${encodeURIComponent(compId)}`}
            className="btn-primary ml-auto inline-flex items-center gap-1.5"
          >
            <Flag className="h-3.5 w-3.5" strokeWidth={2.5} /> Register my clan
          </Link>
        )}
      </div>

      <Card>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-xs font-black uppercase tracking-wider text-forge-300">
            <Users className="h-4 w-4" /> Clans in this comp
          </h2>
          {canManageClans && <CreateClanForm compId={compId} onDone={refreshClans} />}
        </div>

        {clansLoading ? (
          <p className="text-xs text-iron-500">Loading clans…</p>
        ) : (clans ?? []).length === 0 ? (
          <EmptyState
            title="No clans registered yet"
            message={
              canManageClans
                ? 'Create the first clan above, or invite a clan leader to get one started.'
                : 'No clans have been registered to this comp yet. Check back once the comp admins have set them up.'
            }
          />
        ) : (
          <div className="space-y-2">
            {(clans ?? []).map(c => (
              <div
                key={c.id}
                className="flex flex-wrap items-center gap-2 border border-iron-800 bg-iron-950 px-3 py-2"
              >
                <ShieldCheck className="h-4 w-4 text-forge-400" />
                <span className="font-display text-sm font-black text-white truncate">
                  {c.name}
                </span>
                {c.status && (
                  <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-300/80">
                    {c.status}
                  </span>
                )}
                <Link
                  to={`/clan/${encodeURIComponent(c.name)}`}
                  className="ml-auto font-display text-[10px] font-black uppercase tracking-wider text-forge-300 hover:text-forge-200"
                >
                  Open →
                </Link>
              </div>
            ))}
          </div>
        )}
      </Card>

      {canManageClans && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 shrink-0 text-forge-400" strokeWidth={2.5} />
              <div>
                <h2 className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
                  Public competition page
                </h2>
                <p className="mt-1 text-xs text-iron-500">
                  Authoring moved into Setup — manage hero, schedule, prizes, and publish status there.
                </p>
              </div>
            </div>
            <Link
              to={comp?.orgId ? `/admin/setup?orgId=${encodeURIComponent(comp.orgId)}` : '/admin/setup'}
              className="inline-flex shrink-0 items-center gap-1.5 border border-iron-800 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-forge-500 hover:text-forge-200"
            >
              Open in Setup
              <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
            </Link>
          </div>
        </Card>
      )}

    </PageWrapper>
  );
}
